import * as React from 'react';
import { Controller, type Control, type FieldValues, type UseFormSetValue } from 'react-hook-form';
import type { GroupBase, MultiValue, OptionsOrGroups, SingleValue } from 'react-select';

import { Select, type SelectOption } from '../composites/Select';
import { FormField } from '../patterns/FormField';

import { rhfInlineError } from './rhfInlineError';
import type { Reaction, Effect } from './reactions';
import { useReactions } from './useReactions';
import { useEffects } from './useEffects';
import type { RhfFormFieldShellProps, RhfLayoutProps } from './rhfLayout';

function optionValuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;
  return String(a) === String(b);
}

function findOptionByValue(
  options: OptionsOrGroups<SelectOption, GroupBase<SelectOption>>,
  value: unknown,
): SelectOption | null {
  if (value == null || value === '') return null;
  for (const entry of options) {
    if ('options' in entry && Array.isArray(entry.options)) {
      const hit = entry.options.find((o) => optionValuesEqual(o.value, value));
      if (hit) return hit;
    } else if ('value' in entry) {
      const flat = entry as SelectOption;
      if (optionValuesEqual(flat.value, value)) return flat;
    }
  }
  return null;
}

function findOptionsByValues(
  options: OptionsOrGroups<SelectOption, GroupBase<SelectOption>>,
  values: unknown[],
): SelectOption[] {
  const result: SelectOption[] = [];
  for (const entry of options) {
    if ('options' in entry && Array.isArray(entry.options)) {
      result.push(...entry.options.filter((o) => values.some((v) => optionValuesEqual(o.value, v))));
    } else if ('value' in entry) {
      const flat = entry as SelectOption;
      if (values.some((v) => optionValuesEqual(flat.value, v))) result.push(flat);
    }
  }
  return result;
}

export interface RhfSelectProps extends RhfLayoutProps, RhfFormFieldShellProps {
  control: Control<FieldValues>;
  name: string;
  label: string;
  options: OptionsOrGroups<SelectOption, GroupBase<SelectOption>>;
  placeholder?: string;
  required?: boolean;
  semanticId?: string;
  disabled?: boolean;
  isMulti?: boolean;
  isClearable?: boolean;
  isLoading?: boolean;
  noOptionsMessage?: string;
  reactions?: Reaction[];
  effects?: Effect[];
  setValue?: UseFormSetValue<FieldValues>;
}

export const RhfSelect = React.forwardRef<HTMLDivElement, RhfSelectProps>(function RhfSelect(
  {
    control,
    name,
    label,
    options,
    placeholder = 'Select',
    required = false,
    hint,
    tooltip,
    semanticId,
    disabled = false,
    isMulti = false,
    isClearable = true,
    isLoading = false,
    noOptionsMessage,
    reactions,
    effects,
    setValue,
    controlWidth,
  },
  ref,
) {
  const overrides = useReactions(control, reactions);
  const { runEffects } = useEffects(effects, setValue);

  const finalDisabled = overrides.disabled ?? disabled;
  const finalRequired = (overrides.required as boolean) ?? required;
  const finalLabel = (overrides.label as string) ?? label;

  if (overrides.visible === false) return null;

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState, formState }) => {
        const errorText = rhfInlineError(fieldState, { isSubmitted: formState.isSubmitted });

        if (isMulti) {
          const arrayValue: unknown[] = Array.isArray(field.value) ? field.value : [];
          const selectedOptions = findOptionsByValues(options, arrayValue);

          return (
            <FormField
              ref={ref}
              label={finalLabel}
              hint={hint}
              tooltip={tooltip}
              required={finalRequired}
              semanticId={semanticId}
              disabled={finalDisabled}
              error={errorText}
              controlWidth={controlWidth}
            >
              <Select<SelectOption, true>
                name={name}
                inputId={semanticId ?? name}
                options={options as OptionsOrGroups<SelectOption, GroupBase<SelectOption>>}
                value={selectedOptions}
                onChange={(opts) => {
                  const selected = opts as MultiValue<SelectOption>;
                  const values = selected.map((o) => o.value);
                  field.onChange(values);
                  runEffects(values);
                }}
                onBlur={field.onBlur}
                placeholder={placeholder}
                isDisabled={finalDisabled}
                isMulti
                isClearable={isClearable}
                isLoading={isLoading}
                error={Boolean(errorText)}
                semanticId={semanticId}
                backspaceRemovesValue={false}
                noOptionsMessage={() => (isLoading ? 'Loading…' : noOptionsMessage ?? 'No options')}
              />
            </FormField>
          );
        }

        const selectedOption =
          field.value != null && field.value !== '' ? findOptionByValue(options, field.value) : null;

        return (
          <FormField
            ref={ref}
            label={finalLabel}
            hint={hint}
            tooltip={tooltip}
            required={finalRequired}
            semanticId={semanticId}
            disabled={finalDisabled}
            error={errorText}
            controlWidth={controlWidth}
          >
            <Select
              name={name}
              inputId={semanticId ?? name}
              options={options}
              value={selectedOption}
              onChange={(opt) => {
                const o = opt as SingleValue<SelectOption>;
                const newVal = o?.value ?? '';
                field.onChange(newVal);
                runEffects(newVal, o as SelectOption & Record<string, unknown>);
              }}
              onBlur={field.onBlur}
              placeholder={placeholder}
              isDisabled={finalDisabled}
              isClearable={isClearable}
              isLoading={isLoading}
              error={Boolean(errorText)}
              semanticId={semanticId}
              noOptionsMessage={() => (isLoading ? 'Loading…' : noOptionsMessage ?? 'No options')}
            />
          </FormField>
        );
      }}
    />
  );
});

RhfSelect.displayName = 'RhfSelect';
